import { useCallback, useEffect, useMemo, useState } from "react";
import { ruleAttachments } from "../lib/ruleAttachments";
import { useParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { api, ApiError, type PlayerRulePayload } from "../lib/api";
import { tournamentDataRuleTiles } from "../lib/rulesPresentation";
import { splitPublishedRule, splitRuleTextBlocks, type RuleTextBlock } from "../lib/publishedRuleSections";
import { cdmxDayMonthEs } from "../lib/dateFormat";
import Icon from "../components/icons";
import PlayerTournamentBottomNav from "../components/PlayerTournamentBottomNav";
import TournamentSponsorSlots from "../components/TournamentSponsorSlots";
import { PlayerProfileScopeSwitcher } from "../components/PlayerFamilyProfileSwitcher";
import { makeLatestOnly } from "../lib/latestOnly";
import { usePlayerProfileScope } from "../lib/usePlayerProfileScope";

export default function PlayerRulesPage() {
  const { id } = useParams<{ id: string }>();
  const tournamentId = id ? Number(id) : NaN;
  const { token } = useAuth();

  const [payload, setPayload] = useState<PlayerRulePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  // Cuenta familiar: cada hijo inscrito acepta SU reglamento.
  const scope = usePlayerProfileScope({ tournamentId });
  const scopeProfileId = scope.profileId;
  const latestLoad = useMemo(() => makeLatestOnly(), []);

  const load = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId) || !scope.ready) return;
    const attempt = latestLoad.begin();
    setLoading(true);
    setError(null);
    try {
      const response = await api.player.getTournamentRule(token, tournamentId, scopeProfileId);
      if (!latestLoad.isCurrent(attempt)) return;
      setPayload(response.data);
    } catch (err) {
      if (!latestLoad.isCurrent(attempt)) return;
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar el reglamento.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      if (latestLoad.isCurrent(attempt)) setLoading(false);
    }
  }, [latestLoad, scope.ready, scopeProfileId, token, tournamentId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Al cambiar de hijo, el aviso de «aceptado» del anterior ya no aplica.
  useEffect(() => {
    setMessage(null);
  }, [scopeProfileId]);

  // Bug (Eduardo 2026-08-22): esta vista generaba un "machote" por formato
  // (sistema de retos de escalera) aunque el torneo fuera Round Robin — el
  // format_config a nivel torneo es heredado y la modalidad vive por
  // categoría. Misma decisión que el PDF del admin (2026-07-14): el
  // reglamento es SOLO los datos del torneo + lo que el admin publicó.
  const baseTiles = useMemo(() => tournamentDataRuleTiles(payload?.tournament ?? null), [payload]);
  // Maqueta 2026-10-06: las secciones del reglamento (las del formulario del
  // admin o los «## Encabezado» del texto libre) se leen plegables.
  const ruleBlocks = useMemo<RuleTextBlock[]>(() => {
    const parsed = splitPublishedRule(payload?.rule?.content);
    return [
      ...parsed.sections.map((section) => ({ title: section.label, body: section.value })),
      ...splitRuleTextBlocks(parsed.fallback),
    ];
  }, [payload]);
  const [openBlocks, setOpenBlocks] = useState<Record<number, boolean>>({});
  useEffect(() => {
    setOpenBlocks({});
  }, [payload?.rule?.id]);

  async function handleAccept() {
    if (!token || !payload?.rule) return;
    setAccepting(true);
    setMessage(null);
    setError(null);
    try {
      await api.player.acceptTournamentRule(
        token,
        tournamentId,
        payload.rule.id,
        scopeProfileId,
      );
      setMessage("Reglamento aceptado. Gracias.");
      await load();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo registrar la aceptación.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setAccepting(false);
    }
  }

  if (Number.isNaN(tournamentId)) {
    return (
      <section className="card">
        <p className="form-error">ID de torneo inválido.</p>
      </section>
    );
  }

  const rule = payload?.rule ?? null;
  const attachments = rule ? ruleAttachments(rule) : [];
  const publishedOn = cdmxDayMonthEs(rule?.published_at, { withYear: true });
  const acceptedOn = cdmxDayMonthEs(payload?.accepted_at, { withYear: true });
  const eyebrow = rule
    ? [`Versión ${rule.version}`, publishedOn ? `Publicado el ${publishedOn}` : null].filter(Boolean).join(" · ")
    : "Torneo";
  const firstTitled = ruleBlocks.findIndex((block) => block.title !== null);

  return (
    <div className="pt-reto-theme ps-page">
      {/* Secciones del torneo (Eduardo 2026-10-05): en computadora, pestañas
          aquí arriba; en celular, la barra flotante de abajo. */}
      <PlayerTournamentBottomNav tournamentId={tournamentId} />
      <PlayerProfileScopeSwitcher scope={scope} tournament />
      <header className="ps-head">
        <span className="ps-head__eyebrow">{eyebrow}</span>
        <h1>Reglamento</h1>
        {rule?.title && rule.title.trim().toLowerCase() !== "reglamento" ? (
          <p className="ps-head__subtitle">{rule.title}</p>
        ) : null}
      </header>

      {error && (
        <p role="alert" className="form-error">
          {error}
        </p>
      )}
      {message && <p className="form-message">{message}</p>}

      {loading ? (
        <section className="ps-card">
          <p className="muted">Cargando reglamento…</p>
        </section>
      ) : !rule || !payload ? (
        <section className="ps-card">
          <p className="muted">
            Aún no hay un reglamento publicado para este torneo. Cuando el admin lo publique te aparecerá aquí.
          </p>
        </section>
      ) : (
        <>
          <section className="pt-home__reto ps-reto ps-rules-accept" aria-label="Aceptar el reglamento">
            <span className={`ps-status${payload.accepted ? " is-ok" : " is-pending"}`}>
              {payload.accepted ? `Aceptado${acceptedOn ? ` · ${acceptedOn}` : ""}` : "Te falta aceptarlo"}
            </span>
            <p className="ps-rules-accept__copy">
              {payload.accepted
                ? "Ya aceptaste esta versión del reglamento."
                : "Léelo y acéptalo antes de tu primer partido."}
            </p>
            {!payload.accepted ? (
              <button
                type="button"
                className="player-reto-card__report ps-rules-accept__cta"
                onClick={() => void handleAccept()}
                disabled={accepting}
              >
                {accepting ? "Registrando…" : "Acepto el reglamento"}
              </button>
            ) : null}
            {attachments.map((item) => (
              <a
                key={item.url}
                className="player-reto-card__button player-reto-card__button--call ps-rules-accept__pdf"
                href={item.url}
                target="_blank"
                rel="noreferrer"
                download
              >
                <Icon name="download" />
                <span>
                  Descargar PDF{attachments.length > 1 && item.filename ? ` · ${item.filename}` : ""}
                </span>
              </a>
            ))}
          </section>

          {baseTiles.length > 0 ? (
            <section className="ps-card" aria-labelledby="ps-rules-essentials">
              <h2 id="ps-rules-essentials" className="ps-card__title">
                Lo esencial
              </h2>
              <dl className="ps-tiles">
                {baseTiles.map((tile) => (
                  <div key={tile.label} className="ps-tile">
                    <dt>{tile.label}</dt>
                    <dd>{tile.value}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ) : null}

          {ruleBlocks.length > 0 ? (
            <section className="ps-card" aria-labelledby="ps-rules-text">
              <h2 id="ps-rules-text" className="ps-card__title">
                Reglas del torneo
              </h2>
              <div className="ps-accordion">
                {ruleBlocks.map((block, index) => {
                  if (block.title === null) {
                    return (
                      <div key={`block-${index}`} className="ps-rule-text">
                        {block.body}
                      </div>
                    );
                  }
                  const open = openBlocks[index] ?? index === firstTitled;
                  const panelId = `ps-rule-panel-${index}`;
                  return (
                    <div key={`block-${index}`} className={`ps-accordion__item${open ? " is-open" : ""}`}>
                      <button
                        type="button"
                        className="ps-accordion__toggle"
                        aria-expanded={open}
                        aria-controls={panelId}
                        onClick={() => setOpenBlocks((prev) => ({ ...prev, [index]: !open }))}
                      >
                        <span>{block.title}</span>
                        <span className="ps-accordion__sign" aria-hidden="true">
                          {open ? "−" : "+"}
                        </span>
                      </button>
                      <div id={panelId} className="ps-accordion__panel ps-rule-text" hidden={!open}>
                        {block.body || "—"}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ) : null}
        </>
      )}

      <TournamentSponsorSlots compact />
    </div>
  );
}
